import { useScrollReveal } from '@/hooks/useScrollReveal';

const flowSteps = [
  {
    icon: 'ri-seedling-line',
    title: 'Harvest Intake',
    description: 'Fresh produce and seafood arrive at Freshhlock processing centres — origin, harvest date, and supplier details are logged.',
    bgIcon: 'bg-secondary-100',
    textIcon: 'text-secondary-600',
    borderIcon: 'border-secondary-200/60',
    accent: 'bg-secondary-500',
  },
  {
    icon: 'ri-barcode-line',
    title: 'Process & Standardise',
    description: 'Cleaned, graded, precision-weighed, vacuum-packed — and standardised into a SKU code that means the same thing everywhere.',
    bgIcon: 'bg-accent-100',
    textIcon: 'text-accent-600',
    borderIcon: 'border-accent-200/60',
    accent: 'bg-accent-500',
  },
  {
    icon: 'ri-database-2-line',
    title: 'List & Store',
    description: 'Processed SKUs enter verified cold storage. Warehouses list available capacity, matched to in-bound product flow.',
    bgIcon: 'bg-primary-100',
    textIcon: 'text-primary-600',
    borderIcon: 'border-primary-200/60',
    accent: 'bg-brand-500',
  },
  {
    icon: 'ri-link',
    title: 'Match & Move',
    description: 'The platform matches stored inventory with demand — buyers, routes, and reefer capacity converge on the same SKU.',
    bgIcon: 'bg-secondary-100',
    textIcon: 'text-secondary-700',
    borderIcon: 'border-secondary-200/60',
    accent: 'bg-secondary-500',
  },
  {
    icon: 'ri-shopping-bag-3-line',
    title: 'Trade & Deliver',
    description: 'Sold via Wholesale (B2B) or Retail (B2C) — temperature-verified delivery from processor to doorstep, one standard throughout.',
    bgIcon: 'bg-primary-100',
    textIcon: 'text-primary-600',
    borderIcon: 'border-primary-200/60',
    accent: 'bg-brand-500',
  },
];

export default function HowItWorksSection() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section className="py-20 md:py-28 bg-background-50">
      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
        <div className="text-center mb-14 md:mb-20">
          <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-4">
            Platform Mechanics
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-700 mb-4">
            How Freshhlock Works
          </h2>
          <p className="text-foreground-500 max-w-xl mx-auto">
            Five steps that transform India&apos;s fragmented cold chain into a connected, efficient network.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-stretch">
          {flowSteps.map((step, idx) => (
            <div key={step.title} className="relative flex flex-col">
              <div
                className={`flex-1 flex flex-col bg-card-50 rounded-2xl border ${step.borderIcon} p-6 md:p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md group relative overflow-hidden card-animate`}
                style={{ transitionDelay: `${idx * 60}ms` }}
              >
                {/* Colored accent strip */}
                <div className={`absolute left-0 top-4 bottom-4 w-1 rounded-full ${step.accent} opacity-60`}></div>

                <div className="pl-4 flex-1 flex flex-col">
                  {/* Step badge */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-10 h-10 rounded-full ${step.accent} text-paper-50 flex items-center justify-center text-sm font-bold shadow-sm`}>
                      {String(idx + 1).padStart(2, '0')}
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-foreground-400">
                      Step {String(idx + 1).padStart(2, '0')}
                    </span>
                  </div>

                  <div className={`w-14 h-14 flex items-center justify-center rounded-xl ${step.bgIcon} ${step.textIcon} mb-4 animate-icon-float icon-hover-pop`}>
                    <i className={`${step.icon} text-2xl`}></i>
                  </div>

                  <h3 className="font-heading font-bold text-primary-700 text-base mb-2">{step.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed flex-1">{step.description}</p>
                </div>
              </div>

              {/* Arrow — desktop only, centred in the grid gap between adjacent cards */}
              {idx < flowSteps.length - 1 && (
                <div className="hidden lg:flex absolute top-1/2 -right-5 w-5 -translate-y-1/2 items-center justify-center pointer-events-none">
                  <i className="ri-arrow-right-line text-paper-400 text-lg"></i>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Traceability callout */}
        <div className="mt-14 max-w-2xl mx-auto">
          <div className="bg-secondary-50 border border-secondary-200/60 rounded-xl px-6 py-4 flex items-center gap-4">
            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
              <i className="ri-shield-check-line text-lg"></i>
            </div>
            <p className="text-sm text-secondary-800 font-medium">
              Every batch is traceable — from harvest to your freezer, with full documentation and temperature logs.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}